import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { ScheduleProvider } from "../components/context/ScheduleContext";
import { useScheduleRole } from "../utils/useScheduleRole";
import { SCHEDULE_ENTRY_SUBMODULE_SLUG } from "../utils/schedulePermissionSlugs";
import { useScheduleInlineEdit } from "../utils/useScheduleInlineEdit";
import ScheduleEditPanel from "../components/form/ScheduleEditPanel";
import { useScheduleDashboardStats } from "../utils/useScheduleDashboardStats";
import { notifyScheduleDataChanged } from "../utils/scheduleDataEvents";
import OfficeScheduleForm from "../components/form/OfficeScheduleForm";
import ScheduleLiveStatsPanel from "../components/metrics/ScheduleLiveStatsPanel";
import ScheduleDirectorySection from "../components/directory/ScheduleDirectorySection";
import CompleteScheduleModal from "../components/modals/CompleteScheduleModal";
import CancelScheduleModal from "../components/modals/CancelScheduleModal";
import "../styles/scheduleDashboard.css";
import "../styles/scheduleTable.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const ENTRY_QUICK_JUMP = [
  { key: "stats", label: "Summary", targetId: "schedule-stats" },
  { key: "register", label: "Add schedule", targetId: "schedule-entry-form" },
  { key: "list", label: "Schedule directory", targetId: "schedule-list" },
];

function filterQuickJump(items, { showSummary, showCapture, showDirectory, editing }) {
  return items.filter((item) => {
    if (item.key === "stats") return showSummary;
    if (item.key === "register") return showCapture || Boolean(editing);
    if (item.key === "list") return showDirectory;
    return true;
  });
}

function ScheduleEntryContent() {
  const location = useLocation();
  const legacyEditHandled = useRef(false);
  const perms = useScheduleRole(SCHEDULE_ENTRY_SUBMODULE_SLUG);
  const {
    editingSchedule,
    editError,
    openEdit,
    openEditById,
    cancelEdit,
    clearEdit,
  } = useScheduleInlineEdit("schedule-entry-form");

  const showSummary = perms.canView;
  const showCapture = perms.canCreate || (Boolean(editingSchedule) && perms.canEdit);
  const showDirectory = perms.canView;
  const hasAnyAccess = showSummary || showCapture || showDirectory;

  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [completeTarget, setCompleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [registerFormKey, setRegisterFormKey] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const { dashboardStats, loadingStats, refreshStats } = useScheduleDashboardStats({
    enabled: showSummary,
    reloadToken,
    listScope: "self",
  });

  useEffect(() => {
    const editId = location.state?.editScheduleId;
    if (!editId || legacyEditHandled.current) return;
    legacyEditHandled.current = true;
    openEditById(editId);
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state?.editScheduleId, location.pathname, openEditById]);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    clearEdit();
    setReloadToken((t) => t + 1);
    notifyScheduleDataChanged();
    refreshStats({ silent: true });
  };

  const handleEditSuccess = (msg) => {
    handleSuccess(msg);
    cancelEdit();
  };

  const quickJumpItems = filterQuickJump(ENTRY_QUICK_JUMP, {
    showSummary,
    showCapture,
    showDirectory,
    editing: editingSchedule,
  });

  const handleRegisterSuccess = (msg) => {
    handleSuccess(msg);
    setRegisterFormKey((k) => k + 1);
    if (showDirectory) {
      requestAnimationFrame(() => {
        document.getElementById("schedule-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const emptyBaseline = perms.canCreate
    ? "No schedules created by you yet. Use Add schedule above to create the first entry."
    : "No schedules created by you yet.";

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Office administration</p>
          <h2 className="user-builder-hero__title">Schedule entry</h2>
          <p className="user-builder-hero__sub">
            Create and manage your schedule entries. Permissions follow schedule-entry in User builder.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={editError} role="alert" />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !hasAnyAccess ? (
        <Navigate to="/user-login" replace />
      ) : null}
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && showSummary ? (
            <ScheduleLiveStatsPanel loading={loadingStats} dashboardStats={dashboardStats} />
          ) : null}

          {!perms.permissionsLoading && showCapture ? (
            <section
              id="schedule-entry-form"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              {editingSchedule ? (
                perms.canEdit ? (
                  <ScheduleEditPanel
                    schedule={editingSchedule}
                    onCancel={cancelEdit}
                    onSuccess={handleEditSuccess}
                  />
                ) : null
              ) : perms.canCreate ? (
                <>
                  <div className="user-builder-panel__head">
                    <h3 className="user-builder-panel__title">+ Add Schedule</h3>
                    <p className="user-builder-panel__hint">
                      Record a new schedule with type, date, assignee, and priority.
                    </p>
                  </div>
                  <OfficeScheduleForm key={registerFormKey} onSuccess={handleRegisterSuccess} />
                </>
              ) : null}
            </section>
          ) : null}

          {!perms.permissionsLoading && showDirectory ? (
            <div id="schedule-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <ScheduleDirectorySection
                listScope="self"
                showPanelHead
                permissions={perms}
                reloadToken={reloadToken}
                emptyBaseline={emptyBaseline}
                onEdit={perms.canEdit ? openEdit : undefined}
                onComplete={perms.canComplete ? setCompleteTarget : undefined}
                onCancel={perms.canCancel ? setCancelTarget : undefined}
              />
            </div>
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <GlobalQuickJump
            asideAriaLabel="Schedule entry sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <CompleteScheduleModal
        schedule={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />

      <CancelScheduleModal
        schedule={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />
    </div>
  );
}

/** Office — Schedule Entry (`/app/schedules`). */
export default function ScheduleEntryPage() {
  return (
    <ScheduleProvider>
      <ScheduleEntryContent />
    </ScheduleProvider>
  );
}
